import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PermissionService } from '../../services/permission.service';
import { Permission } from '../../models';
import { PermissionDialogComponent } from './permission-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  template: `
    <div class="permissions-container">
      <div class="page-header">
        <h1>Gestión de Permisos</h1>
        <button mat-raised-button color="primary" (click)="openDialog()">
          <mat-icon>add</mat-icon>
          Nuevo Permiso
        </button>
      </div>

      <mat-card class="data-table-container">
        <div class="table-header">
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Buscar permisos</mat-label>
            <input matInput [(ngModel)]="searchTerm" (ngModelChange)="applyFilters()" placeholder="Código o nombre...">
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Módulo</mat-label>
            <mat-select [(ngModel)]="moduleFilter" (ngModelChange)="applyFilters()">
              <mat-option value="">Todos</mat-option>
              <mat-option *ngFor="let module of modules" [value]="module">{{ module }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <table mat-table [dataSource]="filteredPermissions" class="permissions-table">
          <ng-container matColumnDef="code">
            <th mat-header-cell *matHeaderCellDef>Código</th>
            <td mat-cell *matCellDef="let permission">
              <code class="permission-code">{{ permission.code }}</code>
            </td>
          </ng-container>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Nombre</th>
            <td mat-cell *matCellDef="let permission">
              <div class="permission-cell">
                <div class="permission-icon">
                  <mat-icon>{{ getPermissionIcon(permission.code) }}</mat-icon>
                </div>
                <span>{{ permission.name }}</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="module">
            <th mat-header-cell *matHeaderCellDef>Módulo</th>
            <td mat-cell *matCellDef="let permission">
              <span class="module-badge">{{ permission.module }}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="description">
            <th mat-header-cell *matHeaderCellDef>Descripción</th>
            <td mat-cell *matCellDef="let permission">{{ permission.description }}</td>
          </ng-container>

          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef>Creado</th>
            <td mat-cell *matCellDef="let permission">{{ permission.createdAt | date:'dd/MM/yyyy' }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Acciones</th>
            <td mat-cell *matCellDef="let permission">
              <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="Opciones">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="openDialog(permission)">
                  <mat-icon>edit</mat-icon>
                  <span>Editar</span>
                </button>
                <button mat-menu-item (click)="deletePermission(permission)" class="delete-option">
                  <mat-icon>delete</mat-icon>
                  <span>Eliminar</span>
                </button>
              </mat-menu>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>

        <div class="table-footer">
          <span class="results-count">{{ filteredPermissions.length }} permisos configurados</span>
        </div>
      </mat-card>

      <!-- Permission Groups Summary -->
      <div class="permission-groups">
        <h2>Permisos por Módulo</h2>
        <div class="groups-grid">
          <mat-card *ngFor="let group of permissionGroups" class="group-card">
            <mat-card-header>
              <mat-icon mat-card-avatar>{{ getModuleIcon(group.module) }}</mat-icon>
              <mat-card-title>{{ group.module }}</mat-card-title>
              <mat-card-subtitle>{{ group.count }} permisos</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="group-permissions">
                <span *ngFor="let perm of group.permissions" class="perm-chip">
                  {{ perm }}
                </span>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .permissions-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h1 {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin: 0;
      }
    }

    .data-table-container {
      overflow: hidden;
      margin-bottom: 32px;
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
    }

    .table-header {
      display: flex;
      gap: 16px;
      padding: 16px 24px;
      border-bottom: 1px solid var(--border-color);
    }

    .search-field {
      flex: 1;
      max-width: 400px;
    }

    .filter-field {
      width: 180px;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    .permissions-table {
      width: 100%;
    }

    .permission-code {
      display: inline-block;
      background: rgba(183, 28, 28, 0.08);
      padding: 4px 10px;
      border-radius: var(--radius-sm);
      font-size: 12px;
      font-weight: 600;
      color: var(--primary-color);
    }

    .permission-cell {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .permission-icon {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-md);
      background: rgba(183, 28, 28, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: var(--primary-color);
      }
    }

    .module-badge {
      display: inline-flex;
      align-items: center;
      background: rgba(0, 0, 0, 0.08);
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      color: var(--text-secondary);
    }

    .delete-option {
      color: #c62828;
    }

    .table-footer {
      padding: 16px 24px;
      border-top: 1px solid var(--border-color);
    }

    .results-count {
      font-size: 13px;
      color: var(--text-secondary);
    }

    .permission-groups {
      h2 {
        font-family: var(--font-display);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin-bottom: 16px;
      }
    }

    .groups-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 16px;
    }

    .group-card {
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      mat-card-header {
        mat-icon {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-md);
          background: rgba(183, 28, 28, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-color);
        }
      }
    }

    .group-permissions {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .perm-chip {
      display: inline-flex;
      align-items: center;
      background: rgba(183, 28, 28, 0.08);
      color: var(--primary-color);
      padding: 4px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
    }

    ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }
  `]
})
export class PermissionsComponent implements OnInit {
  permissions: Permission[] = [];
  filteredPermissions: Permission[] = [];
  searchTerm = '';
  moduleFilter = '';
  modules: string[] = [];
  permissionGroups: { module: string; count: number; permissions: string[] }[] = [];
  displayedColumns = ['code', 'name', 'module', 'description', 'createdAt', 'actions'];

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private permissionService = inject(PermissionService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.loadPermissions();
  }

  loadPermissions() {
    this.permissionService.getAllPermissions().subscribe({
      next: (permissions) => {
        this.permissions = permissions;
        this.modules = [...new Set(permissions.map(p => p.module).filter(m => m !== undefined))];
        this.buildPermissionGroups();
        this.applyFilters();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.snackBar.open('Error al cargar los permisos', 'Cerrar', { duration: 5000 });
      }
    });
  }

  buildPermissionGroups() {
    const groups: Record<string, string[]> = {};

    this.permissions.forEach(p => {
      const module = p.module || 'General';
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(p.name);
    });

    this.permissionGroups = Object.entries(groups).map(([module, permissions]) => ({
      module,
      count: permissions.length,
      permissions
    }));
  }

  applyFilters() {
    this.filteredPermissions = this.permissions.filter(permission => {
      const matchesSearch = !this.searchTerm ||
        permission.code.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        permission.name.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesModule = !this.moduleFilter || permission.module === this.moduleFilter;

      return matchesSearch && matchesModule;
    });
  }

  getPermissionIcon(code: string): string {
    const action = code.split('.')[1];
    const icons: Record<string, string> = {
      'read': 'visibility',
      'write': 'edit',
      'delete': 'delete',
      'execute': 'play_arrow'
    };
    return icons[action] || 'vpn_key';
  }

  getModuleIcon(module: string): string {
    const icons: Record<string, string> = {
      'Usuarios': 'people',
      'Roles': 'admin_panel_settings',
      'Páginas': 'language',
      'Auditorías': 'security',
      'Reportes': 'assessment',
      'Configuración': 'settings'
    };
    return icons[module] || 'folder';
  }

  openDialog(permission?: Permission) {
    const dialogRef = this.dialog.open(PermissionDialogComponent, {
      width: '500px',
      data: { permission, modules: this.modules }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (permission) {
          this.permissionService.updatePermission(permission.id, result).subscribe({
            next: () => {
              this.snackBar.open('Permiso actualizado correctamente', 'Cerrar', { duration: 3000 });
              this.loadPermissions();
            },
            error: (error) => {
              this.snackBar.open('Error al actualizar el permiso', 'Cerrar', { duration: 5000 });
            }
          });
        } else {
          this.permissionService.createPermission(result).subscribe({
            next: () => {
              this.snackBar.open('Permiso creado correctamente', 'Cerrar', { duration: 3000 });
              this.loadPermissions();
            },
            error: (error) => {
              this.snackBar.open('Error al crear el permiso', 'Cerrar', { duration: 5000 });
            }
          });
        }
      }
    });
  }

  deletePermission(permission: Permission) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Permiso',
        message: `¿Estás seguro de que deseas eliminar el permiso "${permission.name}"? Los roles que lo tengan asignado perderán esta capacidad.`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.permissionService.deletePermission(permission.id).subscribe({
          next: () => {
            this.snackBar.open('Permiso eliminado correctamente', 'Cerrar', { duration: 3000 });
            this.loadPermissions();
          },
          error: (error) => {
            this.snackBar.open('Error al eliminar el permiso', 'Cerrar', { duration: 5000 });
          }
        });
      }
    });
  }
}
